import { useEffect, useRef, useState } from "react";

import type { CalibratedLens, Pose } from "../fisheye/lens";
import { FisheyeRenderer, type Layout } from "../fisheye/renderer";

type VideoWithRvfc = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: () => void) => number;
  cancelVideoFrameCallback?: (id: number) => void;
};

/** Dewarps a fisheye picture in the browser (WebGL2).
 *
 *  The source is a playing <video> (live tiles: every decoded frame is
 *  re-drawn), a canvas another player paints into (recorded playback and
 *  clips, which decode onto a canvas), or a still image (calibration, view
 *  editing). Views are controlled: pass `views`, and `onChange` reports
 *  drags, zooms and clicks on the fisheye while `interactive`.
 */
export function FisheyeView({
  lens,
  video,
  canvas,
  image,
  views,
  active = 0,
  layout = "single",
  inset = true,
  interactive = false,
  onChange,
  onFocus,
  className = "",
}: {
  lens: CalibratedLens;
  video?: HTMLVideoElement | null;
  canvas?: HTMLCanvasElement | null;
  image?: HTMLImageElement | ImageBitmap | null;
  views: Pose[];
  active?: number;
  layout?: Layout;
  inset?: boolean;
  interactive?: boolean;
  onChange?: (views: Pose[], active: number) => void;
  /** What is on screen after each draw (see FisheyeRenderer.onRender). */
  onFocus?: (focus: { view: Pose; aspect: number } | null) => void;
  className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<FisheyeRenderer | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onFocusRef = useRef(onFocus);
  onFocusRef.current = onFocus;
  const [failed, setFailed] = useState("");

  useEffect(() => {
    if (!canvasRef.current || !overlayRef.current) return;
    try {
      const r = new FisheyeRenderer(canvasRef.current, overlayRef.current, lens, views);
      r.onChange = (v, a) => onChangeRef.current?.(v, a);
      r.onRender = (focus) => onFocusRef.current?.(focus);
      rendererRef.current = r;
    } catch (e) {
      setFailed((e as Error).message);
    }
    return () => {
      rendererRef.current?.destroy();
      rendererRef.current = null;
    };
    // The renderer lives as long as the component; props flow in below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const r = rendererRef.current;
    if (!r) return;
    r.lens = lens;
    r.layout = layout;
    r.inset = inset;
    r.interactive = interactive;
    r.setViews(views, active);
  }, [lens, layout, inset, interactive, views, active]);

  useEffect(() => {
    const v = video as VideoWithRvfc | null | undefined;
    if (!v) return;
    let handle = 0;
    let stopped = false;
    const useRvfc = typeof v.requestVideoFrameCallback === "function";
    const pump = () => {
      if (stopped) return;
      const r = rendererRef.current;
      if (r && v.readyState >= 2 && v.videoWidth > 0) {
        r.setFrame(v, v.videoWidth, v.videoHeight);
      }
      handle = useRvfc ? v.requestVideoFrameCallback!(pump) : requestAnimationFrame(pump);
    };
    pump();
    return () => {
      stopped = true;
      if (useRvfc) v.cancelVideoFrameCallback?.(handle);
      else cancelAnimationFrame(handle);
    };
  }, [video]);

  useEffect(() => {
    if (!canvas) return;
    let handle = 0;
    // The painting player gives no per-frame signal, so poll once per display
    // refresh; an unchanged canvas re-upload is a GPU-side copy.
    const pump = () => {
      const r = rendererRef.current;
      if (r && canvas.width > 1 && canvas.height > 1) r.setFrame(canvas, canvas.width, canvas.height);
      handle = requestAnimationFrame(pump);
    };
    pump();
    return () => cancelAnimationFrame(handle);
  }, [canvas]);

  useEffect(() => {
    const r = rendererRef.current;
    if (!r || !image) return;
    const w = image instanceof HTMLImageElement ? image.naturalWidth : image.width;
    const h = image instanceof HTMLImageElement ? image.naturalHeight : image.height;
    r.setFrame(image, w, h);
  }, [image]);

  // Fills its nearest positioned ancestor, like the <video> it stands in for.
  return (
    <div className={`absolute inset-0 ${className}`}>
      <canvas ref={canvasRef} className="absolute inset-0 block h-full w-full" />
      <canvas
        ref={overlayRef}
        className={`absolute inset-0 block h-full w-full touch-none ${
          interactive ? "cursor-grab active:cursor-grabbing" : "pointer-events-none"
        }`}
      />
      {failed && (
        <div className="absolute inset-0 flex items-center justify-center p-3 text-center text-xs text-bf-muted">
          This browser cannot dewarp the fisheye ({failed}).
        </div>
      )}
    </div>
  );
}
